/**
 * Tour Step Renderer Component
 *
 * Custom step content renderer using Material-UI components.
 * Provides rich text content, progress indicators, and action buttons.
 *
 * @author Andres Nava
 * @version 1.0.0
 */

import React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  LinearProgress,
  Avatar,
  Link,
  Paper,
} from "@mui/material";
import {
  LightbulbOutlined as TipIcon,
  Launch as LaunchIcon,
} from "@mui/icons-material";
import { useTheme, alpha } from "@mui/material/styles";
import type { TourStep } from "../../types/onboarding";
import { coerceTourPlainText } from "../../../shared/utils/coerceEventListingText";

/**
 * Tour Step Renderer Props
 */
interface TourStepRendererProps {
  /** Current step */
  step: TourStep;
  /** Current step index (0-based) */
  index: number;
  /** Total number of steps */
  total: number;
  /** Whether to show progress indicator */
  showProgress?: boolean;
  /** Callback when action button is clicked */
  onActionClick?: (action: string) => void;
  /** Optional title id for accessibility */
  titleId?: string;
}

/**
 * Tour Step Renderer Component
 */
export const TourStepRenderer: React.FC<TourStepRendererProps> = ({
  step,
  index,
  total,
  showProgress = true,
  onActionClick,
  titleId,
}) => {
  const theme = useTheme();

  const actions = step.metadata?.actions as
    | Array<{
        label: string;
        action: string;
        variant?: "text" | "outlined" | "contained";
        color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
      }>
    | undefined;

  // Progress indicator (industry standard: compact, clear step position)
  const progress =
    showProgress && total > 1 ? (
      <Box sx={{ mb: 2 }}>
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          sx={{ mb: 1 }}
        >
          {Array.from({ length: total }, (_, i) => (
            <Box
              key={i}
              sx={{
                width: i === index ? 10 : 6,
                height: i === index ? 10 : 6,
                borderRadius: "50%",
                backgroundColor:
                  i <= index
                    ? theme.palette.primary.main
                    : alpha(theme.palette.primary.main, 0.3),
                transition: theme.transitions.create(
                  ["width", "height", "backgroundColor"],
                  {
                    duration: theme.transitions.duration.short,
                  },
                ),
              }}
              aria-label={`Step ${i + 1} ${i === index ? "(current)" : i < index ? "(completed)" : "(upcoming)"}`}
            />
          ))}
        </Stack>

        {/* Linear progress bar */}
        <Box sx={{ position: "relative" }}>
          <LinearProgress
            variant="determinate"
            value={((index + 1) / total) * 100}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              "& .MuiLinearProgress-bar": {
                borderRadius: 3,
                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                transition: theme.transitions.create("transform", {
                  duration: theme.transitions.duration.standard,
                  easing: theme.transitions.easing.easeOut,
                }),
              },
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              color: theme.palette.text.secondary,
              fontSize: "0.75rem",
              fontWeight: 500,
            }}
          >
            {index + 1} / {total}
          </Typography>
        </Box>
      </Box>
    ) : null;

  const stepTitle = coerceTourPlainText(step.title, '');
  const stepContent = coerceTourPlainText(step.content, '');
  const stepTip = step.tip ? coerceTourPlainText(step.tip, '') : '';

  // Content: readable typography, wrap long text so nothing is cut off
  const content = stepContent ? (
      <Typography
        variant="body1"
        sx={{
          color: theme.palette.text.primary,
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
          fontSize: "0.9375rem",
          fontWeight: 400,
          letterSpacing: "0.01em",
          mb:
            stepTip || step.helpUrl || (actions && actions.length > 0) ? 2 : 0,
        }}
      >
        {stepContent}
      </Typography>
    ) : null;

  // Title with icon (compact hierarchy so content isn't pushed down)
  const title = stepTitle ? (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        mb: 2,
        pb: 1.25,
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
      }}
    >
      {step.icon && React.isValidElement(step.icon) ? (
        <Avatar
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: theme.palette.primary.main,
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
            "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
          }}
        >
          {step.icon}
        </Avatar>
      ) : null}
      <Typography
        variant="h6"
        component="h3"
        id={titleId}
        sx={{
          fontWeight: theme.typography.fontWeightBold,
          color: theme.palette.text.primary,
          fontSize: "1.125rem",
          lineHeight: 1.35,
          letterSpacing: "-0.01em",
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {stepTitle}
      </Typography>
    </Stack>
  ) : step.icon && React.isValidElement(step.icon) ? (
    <Box sx={{ mb: 1.5 }}>
      <Avatar
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          bgcolor: alpha(theme.palette.primary.main, 0.12),
          color: theme.palette.primary.main,
          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`,
          "& .MuiSvgIcon-root": { fontSize: "1.25rem" },
        }}
      >
        {step.icon}
      </Avatar>
    </Box>
  ) : null;

  // Tip block: compact, readable, no overflow
  const tip = stepTip ? (
    <Paper
      elevation={0}
      sx={{
        mt: 2,
        p: 1.5,
        bgcolor: alpha(theme.palette.info.main, 0.06),
        borderLeft: `4px solid ${theme.palette.info.main}`,
        borderRadius: 1,
        boxShadow: `0 1px 3px ${alpha(theme.palette.info.main, 0.08)}`,
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="flex-start">
        <TipIcon
          sx={{
            color: theme.palette.info.main,
            fontSize: "1.25rem",
            mt: 0.125,
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 600,
              color: theme.palette.info.main,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.6875rem",
              mb: 0.5,
              display: "block",
            }}
          >
            Tip
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              lineHeight: 1.65,
              fontSize: "0.875rem",
              fontWeight: 400,
              wordBreak: "break-word",
              overflowWrap: "break-word",
            }}
          >
            {stepTip}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  ) : null;

  // Help link
  const helpLink = step.helpUrl ? (
    <Link
      href={step.helpUrl}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        mt: 1.5,
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontSize: "0.875rem",
        fontWeight: theme.typography.fontWeightMedium,
        "&:hover": {
          textDecoration: "underline",
        },
      }}
    >
      Learn more
      <LaunchIcon sx={{ fontSize: "0.875rem" }} />
    </Link>
  ) : null;

  return (
    <Box
      sx={{
        width: "100%",
        minWidth: 0,
        boxSizing: "border-box",
      }}
    >
      {progress}
      {title}
      {content}
      {tip}
      {helpLink}
      {actions && actions.length > 0 && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{
            mt: 3, // Consistent top margin
            flexWrap: "wrap",
            gap: 1.5, // Use gap for consistent spacing
          }}
        >
          {actions.map((action, idx) => (
            <Button
              key={idx}
              variant={action.variant || "contained"}
              color={action.color || "primary"}
              size="medium"
              onClick={() => onActionClick?.(action.action)}
              sx={{
                fontSize: "0.90625rem", // Slightly larger for better readability
                fontWeight: theme.typography.fontWeightSemiBold,
                px: 3, // More horizontal padding
                py: 1.125, // Slightly more vertical padding
                borderRadius: 2,
                textTransform: "none",
                letterSpacing: "0.01em",
                boxShadow:
                  action.variant === "contained"
                    ? `0 2px 6px ${alpha(theme.palette[action.color || "primary"].main, 0.25)}`
                    : "none",
                transition: theme.transitions.create(
                  ["transform", "box-shadow", "background-color"],
                  {
                    duration: theme.transitions.duration.short,
                    easing: theme.transitions.easing.easeOut,
                  },
                ),
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow:
                    action.variant === "contained"
                      ? `0 4px 12px ${alpha(theme.palette[action.color || "primary"].main, 0.35)}`
                      : `0 2px 4px ${alpha(theme.palette.action.hover, 0.2)}`,
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {coerceTourPlainText(action.label, 'Continue')}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default TourStepRenderer;
